import { RepairAction, ServiceAction } from './plan-detail.types';

export const CallCostTypeEnum = {
  STANDARD_MESSAGE: 'STANDARD_MESSAGE',
  IRELAND: 'IRELAND',
  UK: 'UK',
} as const;

export type CallCostType = keyof typeof CallCostTypeEnum;

export interface ProductAttribute {
  label?: string;
  answerType?: string;
  allowedValues?: string[];
  defaultValue?: string;
  isMandatory?: string | boolean;
  validationRules?: string;
  isCombinationAppliance?: boolean;
}

export interface ManufacturerAttribute {
  label?: string;
  answerType?: string;
  allowedValues?: string[];
  defaultValue?: string;
  isMandatory?: boolean;
  validationRules?: string;
  isCombinationAppliance?: boolean;
}

export interface ManageMyRepairResponse {
  planNumber: string;
  parentPlanNumber?: string;
  planState: string;
  repairAction: RepairAction;
  repairPayment: string;
  repairPaymentType?: string;
  serviceAction: ServiceAction;
  inWarrantyContactName?: string;
  inWarrantyContactTel?: string;
  item: MMRItem;
  superCategory?: string;
  directReferral?: MMRDirectReferral;
  openRepair?: MMRClaimDetail;
  openService?: MMRClaimDetail;
  planCategoryCode: string;
  bookingOEM?: string;
  productType: string;
  isGas?: boolean;
  companyCode: string;
  countryCode: string;
  availableClaimTypes: AvailableClaimType[];
  planInWarrantyPeriod: boolean;
  planInWarrantyContactName?: string;
  planInWarrantyContactTel?: string;
  planInWaitPeriod: boolean;
  attributes?: ProductAttribute[];
  productTypeQuestion?: ProductTypeQuestion;
  manufacturers?: ManufacturerAttribute[];
  isTV?: boolean;
}

export interface AvailableClaimType {
  claimTypeID: string;
  claimTypeCode: string;
  claimTypeName: string;
  claimTypeLabel: string;
  claimPaymentRequired?: boolean;
  claimPaymentData?: ClaimPaymentData | Record<string, never>;
}

export interface ClaimPaymentData {
  type: string;
  label: string;
  amount: string;
}

export interface RepairHistoryResponse {
  result: RepairHistoryResponseResult[];
  status: string;
}

export interface RepairHistoryResponseResult {
  claimId: string;
  guid: string;
  claimStatus: string;
  claimStatusLabel: string;
  claimNumber?: null | string;
  claimTypeId: null | string;
  claimTypeCode: null | string;
  claimTypeName: null | string;
  claimTypeLabel: null | string;
  claimNextActionState: ClaimNextActionState;
  prReference?: null | string;
  callbackData?: CallbackData[];
  claimCreatedTimestamp: string;
  repairData: RepairData;
}

export type CallbackData = {
  identifier: string;
  value: string;
};

export type ClaimNextActionState =
  | 'INCOMPLETE_QA'
  | 'INCOMPLETE_SERVICE_OPTION'
  | 'CLAIM_OPEN'
  | 'CLAIM_CANCELLED'
  | 'REPAIR_COMPLETE'
  | 'CLAIM_COMPLETED'
  | 'PR_OFFER_PENDING'
  | 'PR_OFFER_READY'
  | 'VIEW_OPEN_CLAIM_ONLY'
  | 'SHOW_CALLBACK';

export interface RepairData {
  appointmentDateSelected: string;
  appointmentSlotSelected?: string;
  bookingReference: string;
  claimAddress: AddressBlock;
  claimStatus: string;
  contactHours: MMRContactHours;
  excessPaid: boolean;
  faultSelected: string;
  isCancelable: boolean;
  isRebookable: boolean;
  isTrackable: boolean;
  repairType: string;
  repairerDetails: RepairDetails;
  serviceOptionType: ServiceOptionType;
  trackable: boolean;
  history: History[];
}

export interface RepairDetails {
  history: History[];
  repairer: MMRClaimDetailRepairer;
}

export interface History {
  date: string;
  status: string;
}

export interface MMRDirectReferral {
  company_name?: string;
  company_email?: string;
  company_phone?: string;
  company_website?: string;
}

export interface MMRItem {
  manufacturer: string;
  code: string;
  code_description: string;
  model?: string;
  model_number?: string;
  imei?: string;
}

export const ServiceOptionTypeEnum = {
  'Self Send': 'Self Send',
  Courier: 'Courier',
  'Drop off': 'Drop off',
  'Self Fit': 'Self Fit',
} as const;

export type ServiceOptionType = keyof typeof ServiceOptionTypeEnum;

export type AddressBlock = {
  streetName: string;
  localArea: string;
  townCity: string;
  postCode: string;
};

export interface MMRClaimDetail {
  claimType: string;
  id: string;
  claimStatus: string;
  previousClaimStatus: string;
  rebookable: boolean;
  cancelable: boolean;
  appointmentDateSelected: string;
  appointmentSlotSelected?: string;
  trackable: boolean;
  faultSelected: string;
  excessPaid: boolean;
  repairer: MMRClaimDetailRepairer;
  claimAddress: AddressBlock;
  returnAddress?: AddressBlock;
  deliveryAddress?: AddressBlock;
  dropOffAddress?: AddressBlock;
  collectionAddress?: AddressBlock;
  serviceOptionType: ServiceOptionType;
  bookingReference: string;
  customerHasMobileContactNumber: boolean;
  appointmentHistory: MMRAppointmentHistory[];
  bookingOEM: string;
  history: History[];
  claimStatusName: string;
  isClaimsTrackerEnabled: boolean;
  claimTypeId: string | null;
  isTvRemoteReplacement?: boolean;
  paymentInformation?: {
    type: string;
    amount: string;
  };
}

export interface MMRAppointmentHistory {
  bookedBy: string;
  bookedTime: string;
  date: string;
  appointmentDate: string;
  timeSlot: string;
  engineer: string;
  appointmentNotes: string;
  appointmentCompletionStatus: string;
  visitChanges: string;
  engineerName: string;
  appointmentTime: string;
  appointmentTimeStart?: string;
  appointmentTimeEnd?: string;
  bookingOEM: string;
  appointmentIsToday?: boolean;
}

export interface MMRClaimDetailRepairer {
  name?: string;
  phone_number?: string;
  email?: string;
  logo?: string;
  contactHours: MMRContactHours;
  postCode?: string;
}
export interface MMRContactHours {
  monday: MMRContactHoursServiceHours;
  tuesday: MMRContactHoursServiceHours;
  wednesday: MMRContactHoursServiceHours;
  thursday: MMRContactHoursServiceHours;
  friday: MMRContactHoursServiceHours;
  saturday: MMRContactHoursServiceHours;
  sunday: MMRContactHoursServiceHours;
}
export interface MMRContactHoursServiceHours {
  openingTime: string;
  closingTime: string;
}

export interface OrbitFlowData {
  planNumber: string;
  currentState: RepairAction | ServiceAction;
  claimTypeId: string;
  hasServiceOption: boolean;
  claimId?: string;
  claimType: 'REPAIR' | 'SERVICE';
  selectedProductType?: string;
  manufacturer?: string;
}

export interface ProductTypeQuestion {
  options?: string[];
  attributeLabel?: string;
  selectedOption?: string;
  isCombinationAppliance?: boolean;
}
